import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AtSign, KeyRound } from "lucide-react";

/**
 * Admin — create or update login (email + optional new password) for an
 * existing employee. Reuses POST /api/employees/{id}/credentials.
 */
export default function UpdateLoginDialog({ open, onOpenChange, employee, onSaved }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && employee) {
      setEmail(employee.login_email || employee.email || "");
      setPassword("");
    }
  }, [open, employee]);

  const submit = async () => {
    const em = (email || "").trim().toLowerCase();
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { toast.error("Enter a valid email"); return; }
    if (password && password.length < 6) { toast.error("Password ≥ 6 characters"); return; }
    setBusy(true);
    try {
      const body = { email: em };
      if (password) body.password = password;
      const { data } = await api.post(`/employees/${employee.id}/credentials`, body);
      toast.success(data.created ? "Login created" : "Login updated", {
        description: `${em}${data.password_set ? " · new temp password shared" : ""}`,
      });
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error("Update failed", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const gen = () => setPassword(`Emp@${Math.random().toString(36).slice(2, 8)}`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-xl">Update login</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Change the login email for <b>{employee?.name}</b>. Leave password blank to keep it as-is; set a value to reset it (they'll be forced to change on next login).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold flex items-center gap-1.5">
              <AtSign size={12} /> Login email
            </Label>
            <Input
              data-testid="update-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="employee@yourfirm.com"
            />
            {employee?.login_email && employee.login_email !== email ? (
              <div className="text-[10px] text-amber-700 mt-1">
                Currently: <span className="font-mono">{employee.login_email}</span>
              </div>
            ) : null}
          </div>
          <div>
            <div className="flex items-end justify-between">
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold flex items-center gap-1.5">
                <KeyRound size={12} /> New password <span className="text-zinc-400">(optional)</span>
              </Label>
              <button onClick={gen} className="text-[10px] tracking-widest uppercase font-semibold text-zinc-500 hover:text-zinc-900">Generate</button>
            </div>
            <Input
              data-testid="update-login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Leave blank to keep existing"
            />
          </div>
          {employee?.is_locked ? (
            <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 p-2">
              This account is locked after failed attempts. Saving here also unlocks it.
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="update-login-submit" disabled={busy} onClick={submit} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
            {busy ? "Saving…" : "Save login"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
